import { UserPermission } from '@/constants/UserPermission';
import type { RoutePermissionMeta } from '@/types/IRouterMeta';
import type { RouteLocationNormalized } from 'vue-router';

export type RoutePermissionSource = {
  meta?: { permission?: RoutePermissionMeta };
};

/**
 * Concatenate allow/deny from `route.matched` into flat lists.
 * For early exit and debug only — authorization uses {@link getAreAllRouteLevelsAuthorized} on `to.matched`.
 */
export function collectPermissionsForRouteLevels(route: RouteLocationNormalized): Required<RoutePermissionMeta> {
  return route.matched.reduce<Required<RoutePermissionMeta>>(
    (acc, matchedRoute) => {
      const permission = matchedRoute.meta?.permission;
      if (permission?.allow) {
        acc.allow.push(...permission.allow);
      }

      if (permission?.deny) {
        acc.deny.push(...permission.deny);
      }

      return acc;
    },
    { allow: [], deny: [] },
  );
}

/**
 * Whether the user satisfies this route record's `allow` list (OR within the list).
 * Empty list = no allow check on this record. Prefer {@link getAreAllRouteLevelsAuthorized} for full route access.
 */
function hasAnyAllowedPermission({
  permission,
  allowedPermissions,
}: {
  permission: Set<UserPermission>;
  allowedPermissions: UserPermission[];
}): boolean {
  if (allowedPermissions.length === 0) {
    // Empty array = no allow check required
    return true;
  }

  return allowedPermissions.some((perm) => permission.has(perm));
}

function isDeniedByPermission({
  permission,
  deniedPermissions,
  shouldUseAdminBackDoor,
}: {
  permission: Set<UserPermission>;
  deniedPermissions: UserPermission[];
  shouldUseAdminBackDoor?: boolean;
}): boolean {
  return deniedPermissions.some((perm) => {
    if (perm === UserPermission.READ_ONLY_PLATFORM && shouldUseAdminBackDoor) {
      return false;
    }

    return permission.has(perm);
  });
}

/**
 * Whether the user passes this single matched record's permission meta.
 * - `allow`: any one (OR); empty / omitted = no allow check on this record
 * - `deny`: refuse if the user has any (admin back door can skip `READ_ONLY_PLATFORM`)
 */
export function isRouteLevelAuthorized({
  permission,
  routeLevel,
  shouldUseAdminBackDoor,
}: {
  permission: Set<UserPermission>;
  routeLevel: RoutePermissionSource;
  shouldUseAdminBackDoor?: boolean;
}): boolean {
  const { allow = [], deny = [] } = routeLevel.meta?.permission ?? {};

  return (
    hasAnyAllowedPermission({ permission, allowedPermissions: allow }) &&
    !isDeniedByPermission({ permission, deniedPermissions: deny, shouldUseAdminBackDoor })
  );
}

/**
 * Authorization: may this user access this route on this build?
 *
 * 1. Global: requires `UNBLOCK`. Routes with no permission meta never reach this (guard early exit).
 * 2. Global: `SUPER_ADMIN` bypasses all route-level checks.
 * 3. Per matched record (parent → child, all must pass): OR within `allow`, refuse on any `deny`
 *    (`READ_ONLY_PLATFORM` deny skipped when the admin back door is on).
 *
 * Put tier gates (e.g. `PLATFORM`) on parent routes; feature roles on children. Do not list unrelated
 * permissions on one record — OR within a record means any one permission is enough.
 *
 * Registration (does this URL exist?) and catalog (menu presentation) are separate layers.
 * Inactive / blocked accounts use `UNAUTHORIZED` (auth root + failed auth here), not a dedicated route.
 */
export function getAreAllRouteLevelsAuthorized({
  permission,
  routeLevels,
  shouldUseAdminBackDoor,
}: {
  permission: Set<UserPermission>;
  routeLevels: RoutePermissionSource[];
  shouldUseAdminBackDoor?: boolean;
}): boolean {
  if (!permission.has(UserPermission.UNBLOCK)) return false;

  if (permission.has(UserPermission.SUPER_ADMIN)) return true;

  return routeLevels.every((level) =>
    isRouteLevelAuthorized({ permission, routeLevel: level, shouldUseAdminBackDoor }),
  );
}
